/**
 * Meta Pixel + CAPI shared helper.
 *
 * - Every conversion event generates ONE shared event_id and is fired both
 *   from the browser pixel (as eventID) AND server-side (as event_id via
 *   POST /api/meta-capi) so Meta can deduplicate.
 *
 * - PII (email, phone, first name, last name) is NEVER sent to the browser
 *   pixel unhashed; it is passed to the server which SHA-256 hashes it.
 *   The browser pixel receives an "advanced matching" hash for email/phone
 *   only when Facebook's own fbq() advanced matching is used (which hashes
 *   client-side automatically).
 */

const PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function isBrowser() {
  return typeof window !== "undefined";
}

function uuid() {
  if (isBrowser() && window.crypto?.randomUUID) return window.crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function getCookie(name) {
  if (!isBrowser()) return null;
  const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
}

let initialized = false;

export function initPixel() {
  if (!isBrowser() || !PIXEL_ID) return;
  if (initialized) return;
  if (typeof window.fbq !== "function") return;
  window.fbq("init", PIXEL_ID);
  initialized = true;
}

/**
 * Fire browser pixel + optionally the CAPI mirror for the given event.
 * `event_id` is shared between browser + server for deduplication.
 */
async function fireEvent(eventName, { custom_data = {}, user_data = {} } = {}) {
  if (!PIXEL_ID) return;
  const event_id = uuid();

  // 1) Browser pixel
  if (isBrowser() && typeof window.fbq === "function") {
    try {
      window.fbq("track", eventName, custom_data, { eventID: event_id });
    } catch (err) {
      // Ad blockers commonly break this. Swallow.
    }
  }

  // 2) Server-side CAPI mirror
  try {
    await fetch(`${API}/meta-capi`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "omit",
      body: JSON.stringify({
        event_name: eventName,
        event_id,
        event_source_url: isBrowser() ? window.location.href : undefined,
        user_data: {
          ...user_data,
          fbp: getCookie("_fbp"),
          fbc: getCookie("_fbc"),
        },
        custom_data,
      }),
    });
  } catch (err) {
    // Network failure — non-fatal.
  }

  return event_id;
}

export function trackPageView() {
  if (!isBrowser() || !PIXEL_ID) return;
  // Fire browser PageView (Meta pattern — no need for shared eventID here
  // as PageView is usually not the deduped conversion event, but we still
  // mirror server-side for reliability).
  fireEvent("PageView");
}

export function trackLead(data = {}) {
  return fireEvent("Lead", {
    user_data: {
      email: data.email,
      phone: data.phone,
      first_name: data.first_name,
      last_name: data.last_name,
    },
    custom_data: {
      content_name: data.content_name,
      content_category: data.content_category,
    },
  });
}

export function trackPurchase(data = {}) {
  return fireEvent("Purchase", {
    user_data: {
      email: data.email,
      phone: data.phone,
      first_name: data.first_name,
      last_name: data.last_name,
    },
    custom_data: {
      value: data.value,
      currency: data.currency || "EUR",
      content_name: data.content_name,
      content_ids: data.content_ids,
      content_type: data.content_type || "product",
    },
  });
}

export function trackInitiateCheckout(data = {}) {
  return fireEvent("InitiateCheckout", {
    custom_data: {
      value: data.value,
      currency: data.currency || "EUR",
      content_name: data.content_name,
      content_ids: data.content_ids,
    },
  });
}

/**
 * Prepare a Stripe Payment Link URL for a ticket purchase.
 * - Generates a shared event_id (UUID) used for Meta dedup
 * - Appends it as `client_reference_id` on the Stripe URL so the
 *   webhook can read it back and fire CAPI Purchase with the SAME id
 * - Stashes the event_id + ticket details in sessionStorage so the
 *   /thank-you page can fire browser Pixel Purchase with the same id
 */
export function prepareStripeCheckout({ url, ticketType, value, currency = "EUR", contentName }) {
  const event_id = uuid();
  const payload = { event_id, ticket_type: ticketType, value, currency, content_name: contentName, ts: Date.now() };
  if (isBrowser()) {
    try {
      sessionStorage.setItem("last_checkout_event", JSON.stringify(payload));
    } catch (err) {
      // Session storage unavailable — non-fatal, dedup will just skip
    }
  }
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}client_reference_id=${encodeURIComponent(event_id)}`;
}

/**
 * Fire browser Pixel Purchase using the event_id stashed in sessionStorage.
 * Called from /thank-you page. Server-side Purchase (with same event_id) is
 * fired by the Stripe webhook → Meta dedupes both against one another.
 */
export function trackPurchaseFromSession() {
  if (!isBrowser() || !PIXEL_ID) return null;
  let stashed = null;
  try {
    const raw = sessionStorage.getItem("last_checkout_event");
    if (raw) stashed = JSON.parse(raw);
  } catch (err) {
    return null;
  }
  if (!stashed || !stashed.event_id) return null;

  if (typeof window.fbq === "function") {
    try {
      window.fbq(
        "track",
        "Purchase",
        {
          value: stashed.value,
          currency: stashed.currency || "EUR",
          content_name: stashed.content_name,
          content_type: "product",
        },
        { eventID: stashed.event_id }
      );
    } catch (err) {
      // Ad-blocked — server-side CAPI still delivers the Purchase.
    }
  }
  // Clear so refresh doesn't double-fire
  try {
    sessionStorage.removeItem("last_checkout_event");
  } catch (err) {
    // ignore
  }
  return stashed.event_id;
}
